import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Card, Form, Row, Col, Table } from "react-bootstrap";
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import moment from "moment-timezone";
import clsx from "clsx";

import { alertActions, reportsProductsProcessingActions, variantsActions } from "_actions";

import Spinner from "components/Spinner";
import Button from "components/Button";

const getDropdownButtonLabel = ({ placeholderButtonLabel, value }) => {
  if (value && value.some((o) => o.value === "*")) {
    return `${placeholderButtonLabel}: All`;
  } else {
    return `${placeholderButtonLabel}: ${value?.length || 0} selected`;
  }
};

function camelPad(str) {
  return (
    str
      // Look for long acronyms and filter out the last letter
      .replace(/([A-Z]+)([A-Z][a-z])/g, " $1 $2")
      // Look for lower-case letters followed by upper-case letters
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      // Look for lower-case letters followed by numbers
      .replace(/([a-zA-Z])(\d)/g, "$1 $2")
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })
      // Remove any white space left around the word
      .trim()
  );
}

function getRowsErrorKeys(rows) {
  const keysSet = new Set();
  rows.forEach((row) => {
    Object.keys(row.countPerError).forEach((key) => keysSet.add(key));
  });
  return Array.from(keysSet).sort();
}

const BASE_COLUMNS = [
  { key: "username", label: "Agent" },
  { key: "completed", label: "Completed" },
  { key: "submittedToState", label: "Submitted to State" },
  { key: "error", label: "Total Errors" },
  { key: "totals", label: "Totals" },
];

export default function EinProcessing() {
  const dispatch = useDispatch();
  const defaultCat = localStorage.getItem("@EINOption_Status/category") || "EIN";
  const defaultEnableHighlight = localStorage.getItem("@EINOption_Status/enableHighlight") === "true" ? true : false;
  const defaultShowCodes = localStorage.getItem("@EINOption_Status/showCodes") === "true" ? true : false;

  const availableCategories = useSelector(({ authentication: { user } }) =>
    user.availableCategories?.filter((item) => !["ACCOUNTING", "PASSPORT"].includes(item))
  );
  const productsProcessing = useSelector(
    ({ reportsProductsProcessing: { productsProcessing } }) => productsProcessing || []
  );
  const allProductCodes = useSelector(({ variants }) => variants?.allProductCodes || {});
  const rowsErrorKeys = useMemo(() => getRowsErrorKeys(productsProcessing), [productsProcessing]);
  const { loading } = useSelector(({ reportsProductsProcessing }) => reportsProductsProcessing);

  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [dateRange, setDateRange] = useState("today");
  const [category, setCategory] = useState(availableCategories.includes(defaultCat) ? defaultCat : "EIN");
  const [enableHighlight, setEnableHighlight] = useState(defaultEnableHighlight || false);
  const [showCodes, setShowCodes] = useState(defaultShowCodes || false);
  const [productCodes, setProductCodes] = useState([]);

  const productCodesOptions = useMemo(() => {
    const options = [{ label: "All", value: "*" }];
    if (!category) {
      const allOptions = Object.values(allProductCodes)
        .flat()
        .map((item) => ({
          label: showCodes ? item?.code : item?.customerName || item?.code,
          value: item?.code,
        }));
      options.push(...allOptions);
    } else {
      const categoryOptions = (allProductCodes[category] || []).map((item) => ({
        label: showCodes ? item?.code : item?.customerName || item?.code,
        value: item?.code,
      }));
      options.push(...categoryOptions);
    }

    return options;
  }, [allProductCodes, category, showCodes]);

  const allColumns = useMemo(() => {
    const baseCols = BASE_COLUMNS.filter(
      ({ key }) => !(!["", "INC", "INC_States"].includes(category) && key === "submittedToState")
    );

    return [
      ...baseCols.filter((col) => col.key !== "error" && col.key !== "totals"),
      { key: "error", label: "Total Errors" },
      ...rowsErrorKeys.map((key) => ({
        key: `error_${key}`,
        label: camelPad(key),
        isError: true,
      })),
      ...baseCols.filter((col) => col.key === "totals"),
    ];
  }, [category, rowsErrorKeys]);

  const productsProcessingReport = () => {
    const productCodesArray = productCodes?.filter((item) => item?.value !== "*")?.map((item) => item?.value) || [];

    if (productCodesArray.length > 30) {
      dispatch(alertActions.error("Select no more than 30 product codes"));
      return;
    }

    dispatch(
      reportsProductsProcessingActions.getAll({
        from: startDate,
        to: endDate,
        category,
        productCodes: productCodesArray,
      })
    );
  };

  const handleChangeCategory = ({ target: { value } }) => {
    setCategory(value);
    setProductCodes([]);
    localStorage.setItem("@EINOption_Status/category", value);
  };

  const handleChangeProductCodes = (values = [], event) => {
    const isSelectAllExist = productCodesOptions?.find((item) => item?.value === "*");
    if (isSelectAllExist) {
      if (event.option.value === "*") {
        if (event.action === "select-option") {
          setProductCodes(productCodesOptions);
        }

        if (event.action === "deselect-option") {
          setProductCodes([]);
        }
      } else {
        if (event.action === "select-option") {
          if (values.length === productCodesOptions.length - 1) {
            setProductCodes(productCodesOptions);
          } else {
            setProductCodes(values);
          }
        }

        if (event.action === "deselect-option") {
          setProductCodes(values.filter((item) => item?.value !== "*"));
        }
      }
    } else {
      setProductCodes(values);
    }
  };

  const handleChangeShowCodes = (value) => {
    setShowCodes(value);
    localStorage.setItem("@EINOption_Status/showCodes", value);
  };

  const handleChangeEnableHighlight = (value) => {
    setEnableHighlight(value);
    localStorage.setItem("@EINOption_Status/enableHighlight", value);
  };

  useEffect(() => {
    switch (dateRange) {
      case "today":
        setStartDate(moment().format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        break;

      case "yesterday":
        setStartDate(moment().subtract(1, "day").format("YYYY-MM-DD"));
        setEndDate(moment().subtract(1, "day").format("YYYY-MM-DD"));
        break;

      case "weektodate":
        setStartDate(moment().startOf("week").format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        break;

      case "lastweek":
        setStartDate(moment().day(1).subtract(7, "day").format("YYYY-MM-DD"));
        setEndDate(moment().day(7).subtract(7, "day").format("YYYY-MM-DD"));
        break;

      case "monthtodate":
        setStartDate(moment().startOf("month").format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        break;

      case "pastmonth":
        setStartDate(moment().subtract(1, "months").format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        break;

      case "lastmonth":
        setStartDate(moment().subtract(1, "months").date(1).format("YYYY-MM-DD"));
        setEndDate(moment().startOf("month").subtract(1, "day").format("YYYY-MM-DD"));
        break;

      default:
        break;
    }
  }, [dateRange]);

  useEffect(() => {
    productsProcessingReport();
    dispatch(variantsActions.getAllProductCodes([...availableCategories.map((category) => ({ category }))]));
  }, []);

  const renderTableHeader = () => (
    <thead>
      <tr>
        {allColumns.map((column, idx) => {
          if (column.isError) {
            return (
              <th key={column.key} className="error-header">
                {column.label}
              </th>
            );
          }
          return <th key={column.key}>{column.label}</th>;
        })}
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    if (!productsProcessing.length) {
      return (
        <tbody>
          <tr>
            <td colSpan={allColumns.length} className="text-center">
              No Results
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody>
        {productsProcessing.map((row) => (
          <tr key={`row-${row.username}`} className={row.isError ? "error-row" : ""}>
            {allColumns.map((column) => {
              if (row.hasOwnProperty(column.key)) {
                return <td key={`${row.username}_${column.key}`}>{row[column.key]}</td>;
              }

              if (column.isError) {
                const errorKey = column.key.replace("error_", "");
                return (
                  <td key={`${row.username}_${column.key}`} className="error-cell">
                    {row.countPerError[errorKey] || 0}
                  </td>
                );
              }

              return <td key={`${row.username}_${column.key}`}>-</td>;
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <>
      <Container fluid className="mt-5 py-4">
        <Card className="p-2">
          <Row>
            <Col>
              <Form>
                <Form.Row>
                  <Form.Group as={Col} md>
                    <Form.Label>Date Range:</Form.Label>
                    <Form.Control
                      size="sm"
                      as="select"
                      name="dateRange"
                      value={dateRange}
                      onChange={({ target: { value } }) => setDateRange(value)}
                    >
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="weektodate">Week to Date</option>
                      <option value="lastweek">Last Week</option>
                      <option value="monthtodate">Month to Date</option>
                      <option value="pastmonth">Past Month</option>
                      <option value="lastmonth">Last Month</option>
                    </Form.Control>
                  </Form.Group>

                  <Form.Group as={Col} md>
                    <Form.Label>Start Date:</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      name="startDate"
                      max={moment().format("YYYY-MM-DD")}
                      value={startDate}
                      onChange={({ target: { value } }) => setStartDate(value)}
                    />
                  </Form.Group>

                  <Form.Group as={Col} md>
                    <Form.Label>End Date:</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      name="endDate"
                      max={moment().format("YYYY-MM-DD")}
                      value={endDate}
                      onChange={({ target: { value } }) => setEndDate(value)}
                    />
                  </Form.Group>

                  <Form.Group as={Col} md>
                    <Form.Label>Category</Form.Label>
                    <Form.Control size="sm" as="select" value={category} onChange={handleChangeCategory}>
                      <option value="">All</option>
                      {availableCategories.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group as={Col} md>
                    <div className="d-flex align-items-center">
                      <Form.Label>Product Codes</Form.Label>
                      <div className="ml-auto" style={{ color: "gray", fontSize: "14px" }}>
                        <Form.Check
                          id="show-codes-checkbox"
                          className="py-0"
                          type="checkbox"
                          label="Show codes"
                          name="show-codes-checkbox"
                          checked={showCodes}
                          onChange={({ target: { checked } }) => handleChangeShowCodes(checked)}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </div>
                    </div>
                    <ReactMultiSelectCheckboxes
                      className="reactMultiSelectCheckboxes"
                      placeholderButtonLabel="Select Product Codes"
                      name="productCodes"
                      value={productCodes}
                      options={productCodesOptions}
                      onChange={handleChangeProductCodes}
                      getDropdownButtonLabel={getDropdownButtonLabel}
                    />
                  </Form.Group>
                </Form.Row>
              </Form>
            </Col>

            <Col md={2} style={{ display: "flex", alignItems: "center" }}>
              <Button size="sm" className="mr-2" isLoading={loading} onClick={productsProcessingReport}>
                Filter
              </Button>
            </Col>
          </Row>
        </Card>

        <div className="d-flex justify-content-end mr-2" style={{ color: "gray", fontSize: "14px", gap: "20px" }}>
          {/* <Form.Check
            id="show-codes-checkbox"
            className="py-0"
            type="checkbox"
            label="Show codes"
            name="show-codes-checkbox"
            checked={showCodes}
            onChange={({ target: { checked } }) => handleChangeShowCodes(checked)}
          /> */}

          <Form.Check
            id="enable-highlight-checkbox"
            className="py-0"
            type="checkbox"
            label="Show highlighting"
            name="enable-highlight-checkbox"
            checked={enableHighlight}
            onChange={({ target: { checked } }) => handleChangeEnableHighlight(checked)}
          />
        </div>
        <Card>
          <Table responsive className={clsx({ "orders-table": true, "processing-report-table": enableHighlight })}>
            {renderTableHeader()}
            {!loading && renderTableBody()}
          </Table>
          {!!loading && <Spinner />}
        </Card>
      </Container>
    </>
  );
}
